import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { Link } from "react-router-dom";
import { absUrl } from "@/lib/api";

const FILTERS = ["All", "Residential", "Commercial", "Industrial", "CCTV / Security"];

export default function ProjectGallery({ projects = [], loading = false }) {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null); // project object
  const [mediaIndex, setMediaIndex] = useState(0);

  const filtered = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  const openProject = (p) => {
    setActive(p);
    setMediaIndex(0);
  };
  const close = () => setActive(null);

  useEffect(() => {
    const onKey = (e) => {
      if (!active) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") setMediaIndex((i) => Math.min(i + 1, (active.media?.length || 1) - 1));
      if (e.key === "ArrowLeft") setMediaIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <div data-testid="project-gallery">
      <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`gallery-filter-${f.replace(/[^a-z]/gi, "").toLowerCase()}`}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
              filter === f
                ? "border-amber-400 bg-amber-400 text-[#0A0A0C]"
                : "border-[#2a2a34] text-neutral-300 hover:border-amber-400/50"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="aspect-[4/3] animate-pulse rounded-xl bg-[#15151c]" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p, idx) => {
            const cover = p.media?.[0];
            const isVideo = cover?.type === "video";
            const src = absUrl(isVideo ? cover?.poster || cover?.url : cover?.url);
            return (
              <button
                key={p.id}
                onClick={() => openProject(p)}
                className="group relative block aspect-[4/3] w-full overflow-hidden rounded-xl border border-[#22222c] bg-[#141419] text-left"
                data-testid={`project-card-${p.id.slice(0, 6)}`}
              >
                {src && isVideo && !cover?.poster ? (
                  <video src={src} muted playsInline preload="metadata" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : src ? (
                  <img src={src} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-neutral-600">
                    <ImageOff className="h-8 w-8" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <span className="absolute left-3 top-3 rounded-full bg-amber-400/90 px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-wide text-[#0A0A0C]">
                  {p.category}
                </span>
                {isVideo && (
                  <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur">
                    <Play className="h-4 w-4 fill-current" />
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 p-4">
                  {p.service && <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-amber-400">{p.service}</span>}
                  <h3 className="font-display text-lg font-bold uppercase text-white">{p.title}</h3>
                  {p.summary && <p className="mt-1 line-clamp-1 text-sm text-neutral-300">{p.summary}</p>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="project-lightbox"
          >
            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={close} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative z-10 w-full max-w-4xl overflow-hidden rounded-2xl border border-[#26262f] bg-[#0f0f14]"
            >
              <button
                onClick={close}
                className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                aria-label="Close"
                data-testid="lightbox-close"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="relative aspect-video bg-black">
                <LightboxMedia item={active.media?.[mediaIndex]} title={active.title} />
                {active.media?.length > 1 && (
                  <>
                    <NavBtn dir="left" disabled={mediaIndex === 0} onClick={() => setMediaIndex((i) => Math.max(i - 1, 0))} />
                    <NavBtn dir="right" disabled={mediaIndex === active.media.length - 1} onClick={() => setMediaIndex((i) => Math.min(i + 1, active.media.length - 1))} />
                  </>
                )}
              </div>

              <div className="p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="kx-badge">{active.category}</span>
                  {active.service && <span className="rounded-full border border-[#2a2a34] px-3 py-1 text-xs font-semibold text-neutral-300">{active.service}</span>}
                </div>
                <h3 className="mt-3 font-display text-2xl font-bold uppercase text-white">{active.title}</h3>
                {active.summary && <p className="mt-2 text-neutral-400">{active.summary}</p>}
                {active.media?.length > 1 && (
                  <p className="mt-2 text-xs text-neutral-500">
                    {mediaIndex + 1} / {active.media.length}
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LightboxMedia({ item, title }) {
  if (!item) return null;
  if (item.type === "video") {
    return (
      <video key={item.url} src={absUrl(item.url)} poster={absUrl(item.poster)} controls playsInline preload="metadata" className="h-full w-full" data-testid="lightbox-video">
        Your browser does not support the video tag.
      </video>
    );
  }
  return <img src={absUrl(item.url)} alt={title} className="h-full w-full object-contain" />;
}

function NavBtn({ dir, onClick, disabled }) {
  const Icon = dir === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`absolute top-1/2 -translate-y-1/2 ${dir === "left" ? "left-3" : "right-3"} flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white transition-opacity hover:bg-black/80 disabled:opacity-30`}
      aria-label={dir === "left" ? "Previous" : "Next"}
    >
      <Icon className="h-6 w-6" />
    </button>
  );
}

function EmptyState() {
  return (
    <div className="mt-8 rounded-2xl border border-dashed border-[#2a2a34] bg-[#101017] p-10 text-center sm:p-16" data-testid="gallery-empty">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-400/10">
        <ImageOff className="h-8 w-8 text-amber-400" />
      </div>
      <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white">Project gallery coming soon</h3>
      <p className="mx-auto mt-3 max-w-lg text-neutral-400">
        We're curating real Kensmarc Electric job photos and videos — residential, commercial, industrial,
        panel work, lighting, and CCTV installations — to showcase here.
      </p>
      <Link
        to="/request-a-quote"
        className="mt-6 inline-flex rounded-full bg-amber-400 px-6 py-3 text-sm font-bold uppercase tracking-wide text-[#0A0A0C] hover:bg-amber-300"
        data-testid="gallery-empty-quote"
      >
        Start your project
      </Link>
    </div>
  );
}
